import { useState, useEffect, useCallback } from "react";
import FC2ProductCard, { type FC2Product } from "./FC2ProductCard";

const EDGE_FUNCTION_URL = "https://hkiyntojlhhckusmvdhr.supabase.co/functions/v1/fc2-feed";

interface FC2SectionProps {
  type: "new" | "recommended";
  title: string;
  subtitle: string;
  affiliateId?: string;
  minPrice?: number;
  limit?: number;
  accentColor?: string;
}

export default function FC2Section({
  type,
  title,
  subtitle,
  affiliateId,
  minPrice = 3000,
  limit = 12,
  accentColor = "#e11d48",
}: FC2SectionProps) {
  const [products, setProducts] = useState<FC2Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        type,
        minPrice: String(minPrice),
        limit: String(limit),
        adult: "true",
      });
      if (affiliateId) {
        params.set("affiliateId", affiliateId);
      }
      const res = await fetch(`${EDGE_FUNCTION_URL}?${params.toString()}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "取得失敗");
      setProducts(data.products || []);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : "取得に失敗しました");
    } finally {
      setLoading(false);
    }
  }, [type, minPrice, limit, affiliateId]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const skeletonCount = Math.min(limit, 6);

  return (
    <section className="w-full mb-12">
      {/* セクションヘッダー */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div
            className="w-1 h-7 rounded-full flex-shrink-0"
            style={{ background: accentColor }}
          />
          <div>
            <h2 className="text-base font-bold text-gray-800 leading-tight">{title}</h2>
            <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {lastUpdated && (
            <span className="text-xs text-gray-400 whitespace-nowrap hidden sm:block">
              {lastUpdated.toLocaleTimeString("ja-JP", { hour: "2-digit", minute: "2-digit" })} 更新
            </span>
          )}
          <button
            onClick={fetchProducts}
            disabled={loading}
            className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 border border-gray-200 rounded-md px-2.5 py-1.5 cursor-pointer whitespace-nowrap disabled:opacity-50 transition-colors"
          >
            <i className={`ri-refresh-line text-xs ${loading ? "animate-spin" : ""}`} />
            更新
          </button>
        </div>
      </div>

      {/* コンテンツ */}
      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {Array.from({ length: skeletonCount }).map((_, i) => (
            <div key={i} className="bg-white rounded-xl overflow-hidden border border-gray-100 animate-pulse">
              <div className="w-full aspect-[3/4] bg-gray-100" />
              <div className="p-2.5 space-y-2">
                <div className="h-3 bg-gray-100 rounded w-full" />
                <div className="h-3 bg-gray-100 rounded w-3/4" />
                <div className="h-3 bg-gray-100 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-14 text-gray-400 border border-dashed border-gray-200 rounded-xl bg-white">
          <i className="ri-error-warning-line text-2xl mb-2 text-rose-300" />
          <p className="text-sm font-medium text-gray-500 mb-1">商品の取得に失敗しました</p>
          <p className="text-xs text-gray-300 mb-4 text-center max-w-xs">{error}</p>
          <button
            onClick={fetchProducts}
            className="text-xs text-white bg-rose-500 hover:bg-rose-600 rounded-lg px-4 py-2 cursor-pointer transition-colors"
          >
            再試行
          </button>
        </div>
      ) : products.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-14 text-gray-400 border border-dashed border-gray-200 rounded-xl bg-white">
          <i className="ri-inbox-line text-2xl mb-2" />
          <p className="text-sm">該当する商品が見つかりませんでした</p>
          <p className="text-xs text-gray-300 mt-1">条件を変更してお試しください</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {products.map((product) => (
            <FC2ProductCard
              key={product.id}
              product={product}
              affiliateId={affiliateId}
            />
          ))}
        </div>
      )}
    </section>
  );
}
